
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useMutation } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";

import enUK from "@/locales/project/en-UK.json";

import { UPDATE_EXPENSE, DELETE_EXPENSE } from "@/graphql/expenses/mutations";

import FormItem from "@/components/form/FormItem.vue";
import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";

import { Expense, Project } from "@/helpers/project/typesDefinition";
import { formatPrice, parsePriceToCents } from "@/helpers/utils";
import { DocumentNode } from "graphql";

type MessageSchema = typeof enUK;

export default defineComponent({
  props: {
    project: {
      type: Object as PropType<Project>,
      required: true,
    },
    expense: {
      type: Object as PropType<Expense>,
      required: true,
    },
    refetchQueries: {
      type: Array as PropType<{ query: DocumentNode; variables?: object }[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props, context) {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();

    console.log(JSON.stringify(props.expense, null, 2));
    const expenseId = ref(props.expense.id);
    const name = ref(props.expense.name);
    const costNet = ref(props.expense.costNet);
    const costGross = ref(props.expense.costGross);
    const quantity = ref(props.expense.quantity);
    const date = ref(props.expense.date);
    const alreadyInvoiced = ref(props.expense.alreadyInvoiced);

    const updateVariables = computed(() => ({
      id: props.expense.id,
      name: name.value,
      date: date.value,
      costNet: costNet.value,
      costGross: costGross.value,
      quantity: quantity.value,
      alreadyInvoiced: alreadyInvoiced.value,
    }));

    const v$ = useVuelidate(
      {
        id: required,
        name: required,
        date: required,
        costNet: required,
        costGross: required,
        quantity: required,
      },
      updateVariables,
      {
        $autoDirty: true,
      },
    );

    const {
      mutate: updateExpense,
      onDone: onDoneUpdateExpense,
      onError: onErrorUpdateExpense,
      loading: loadingUpdateExpense,
    } = useMutation(UPDATE_EXPENSE, () => ({
      variables: updateVariables.value,
      refetchQueries: props.refetchQueries,
    }));

    onDoneUpdateExpense(async (result) => {
      if (result.data?.updateExpense?.status === "ok") {
        expenseId.value = result.data.updateExpense.data?.id;
        toast.success(t("success.expenses.update"));
        context.emit("close");
      } else {
        const errorResponse = result.data?.updateExpense?.error;
        console.log(errorResponse);
        toast.error(t("errors.expenses.update", { message: errorResponse }));
      }
    });

    onErrorUpdateExpense((error) => {
      logErrorMessages(error);
      toast.error(
        t("errors.expenses.update", { message: t("errors.internal") }),
      );
    });

    const deleteVariables = computed(() => ({
      id: props.expense.id,
    }));

    const {
      mutate: deleteExpense,
      onDone: onDoneDeleteExpense,
      onError: onErrorDeleteExpense,
      loading: loadingDeleteExpense,
    } = useMutation(DELETE_EXPENSE, () => ({
      variables: deleteVariables.value,
      refetchQueries: props.refetchQueries,
    }));

    onDoneDeleteExpense(async (result) => {
      if (result.data?.deleteExpense?.status === "ok") {
        expenseId.value = result.data.deleteExpense.data?.id;
        toast.success(t("success.expenses.delete"));
        context.emit("close");
      } else {
        const errorResponse = result.data?.deleteExpense?.error;
        console.log(errorResponse);
        toast.error(t("errors.expenses.delete", { message: errorResponse }));
      }
    });

    onErrorDeleteExpense((error) => {
      logErrorMessages(error);
      toast.error(
        t("errors.expenses.delete", { message: t("errors.internal") }),
      );
    });

    const loading = loadingDeleteExpense.value || loadingUpdateExpense.value;

    return {
      loading,
      updateExpense,
      deleteExpense,
      formatPrice,
      parsePriceToCents,
      toast,
      name,
      costNet,
      costGross,
      date,
      quantity,
      alreadyInvoiced,
      t,
      v$,
    };
  },
  watch: {},
  emits: ["close"],
  computed: {},
  methods: {
    async submitForm() {
      const isFormCorrect = await this.v$.$validate();
      if (isFormCorrect) {
        return this.updateExpense();
      }
    },
    closeModal(): void {
      this.$emit("close");
    },
  },
  components: {
    Modal,
    FormItem,
    BasicButton,
  },
});
